import { useMemo } from 'react';
import useLocalize from '../../utils/hooks/useLocalize';
import './ScanResultsTable.scss';

/**
 * ScanResultsTable component that displays scan results with dynamic columns.
 * Columns are determined by the scan results data structure.
 *
 * @param {Array} scanResults - Array of scan result objects from JSON
 * @param {boolean} isLoading - Loading state indicator
 * @param {function} onRowClick - Optional callback when a row is clicked
 * @param {function} onSymbolClick - Optional callback when a symbol is clicked
 * @returns React component that renders the scan results table
 */
const ScanResultsTable = ({
  scanResults = [],
  isLoading = false,
  onRowClick,
  onSymbolClick
}) => {
  const localize = useLocalize();

  // Generate dynamic columns from data
  const columns = useMemo(() => {
    if (!scanResults || scanResults.length === 0) {
      return [];
    }

    // Get header name for a field
    const getHeaderName = (field) => {
      const headerMap = {
        symbol: localize('scan.label.symbol') || 'Symbol',
        securityType: localize('scan.criteria.SecurityType') || 'Security Type',
        exchange: localize('scan.criteria.Exchange') || 'Exchange'
      };

      return headerMap[field] || field.charAt(0).toUpperCase() + field.slice(1);
    };

    const firstResult = scanResults[0];
    const cols = [];

    // Always include symbol first if it exists
    if ('symbol' in firstResult) {
      cols.push({
        key: 'symbol',
        header: getHeaderName('symbol'),
        isSymbol: true
      });
    }

    // Add other columns dynamically
    Object.keys(firstResult).forEach((key) => {
      if (key !== 'symbol') {
        cols.push({
          key: key,
          header: getHeaderName(key),
          isSymbol: false
        });
      }
    });

    return cols;
  }, [scanResults, localize]);

  // Format cell value
  const formatCellValue = (value) => {
    if (value == null || value === '') return '-';
    if (typeof value === 'object') {
      // Handle displayValues object - show raw values only
      if (Object.keys(value).length === 0) return '-';

      // Return all display values separated by commas (raw values only)
      return Object.values(value).join(', ');
    }
    return String(value);
  };

  if (isLoading) {
    return (
      <div className="scan-results-table-container">
        <div className="loading-state">
          {localize('scan.results.loading') || 'Loading scan results...'}
        </div>
      </div>
    );
  }

  if (!scanResults || scanResults.length === 0) {
    return (
      <div className="scan-results-table-container">
        <div className="empty-state">
          {localize('scan.results.empty') || 'No scan results to display'}
        </div>
      </div>
    );
  }

  return (
    <div className="scan-results-table-container">
      {/* Header Row */}
      <div className="table-header">
        {/* Fixed first column (Symbol) */}
        {columns.length > 0 && columns[0].isSymbol && (
          <div className="header-cell-fixed">
            <div className="header-content">
              <div className="header-text">{columns[0].header}</div>
            </div>
          </div>
        )}

        {/* Scrollable columns */}
        <div className="header-scrollable">
          {columns.slice(1).map((column) => (
            <div key={column.key} className="header-cell">
              <div className="header-content">
                <div className="header-text">{column.header}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Data Rows */}
      {scanResults.map((result, index) => (
        <div
          key={result.symbol || result.id || index}
          onClick={() => onRowClick && onRowClick(result, index)}
          className="table-row"
        >
          {/* Fixed first column (Symbol) */}
          {columns.length > 0 && columns[0].isSymbol && (
            <div className="body-cell-fixed">
              <div className="symbol-content">
                <div
                  className="symbol-name"
                  onClick={(e) => {
                    e.stopPropagation();
                    onSymbolClick && onSymbolClick(result[columns[0].key]);
                  }}
                >
                  {formatCellValue(result[columns[0].key])}
                </div>
                <div className="symbol-details">
                  {/* Additional symbol details can go here */}
                  {result.code && result.exchange
                    ? `${result.code} ${result.exchange}`
                    : ''}
                </div>
              </div>
            </div>
          )}

          {/* Scrollable data cells */}
          <div className="body-scrollable">
            <div className="body-cells-container">
              {columns.slice(1).map((column) => (
                <div key={column.key} className="body-cell">
                  <div className="cell-content">
                    {formatCellValue(result[column.key])}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default ScanResultsTable;
